import React, { useState, useMemo, useEffect } from "react";
import "./debt.less";
import { Reit, YearData, DebtProfile } from "../../../../types/reit.types";
import { safeFormat } from "../../../../utils/financialHelper";

interface DebtProps {
  reit: Reit | null;
}

interface DebtRow {
  metric: string;
  values: (string | number)[];
}

const Debt: React.FC<DebtProps> = ({ reit }) => {
  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [year, setYear] = useState<string>("");

  const years = useMemo(() => {
    if (!reit?.years) return [];
    return reit.years.map((y: YearData) => `FY ${y.year}`);
  }, [reit]);

  useEffect(() => {
    if (years.length && !year) {
      setYear(years[0]);
    }
  }, [years, year]);

  const debtData: DebtRow[] = useMemo(() => {
    if (!reit?.years?.length || !year) return [];

    const yearValue = year.replace("FY ", "");
    const yearData = reit.years.find((y) => y.year === yearValue);
    if (!yearData) return [];

    if (view === "Yearly") {
      const debt: DebtProfile = yearData.debtProfile;
      return Object.keys(debt).map((key) => ({
        metric: debt[key as keyof DebtProfile]?.key ?? key,
        values: [safeFormat(debt[key as keyof DebtProfile]?.value)],
      }));
    } else {
      const quarters = (yearData as any).quarters || [];
      if (!quarters.length) return [];

      const keys = Object.keys((quarters[0]?.debtProfile as DebtProfile) || {});

      return keys.map((key) => ({
        metric:
          quarters[0]?.debtProfile?.[key as keyof DebtProfile]?.key ?? key,
        values: quarters.map((q: { debtProfile: DebtProfile }) =>
          safeFormat(q.debtProfile?.[key as keyof DebtProfile]?.value)
        ),
      }));
    }
  }, [reit, year, view]);

  return (
    <section className="debt">
      <div className="debt__card">
        {/* Header */}
        <div className="debt__header">
          <h2>Debt Profile</h2>

          <div className="debt__subheader">
            {/* Toggle */}
            <div className="debt__tabs">
              <button
                className={view === "Yearly" ? "active" : ""}
                onClick={() => setView("Yearly")}
              >
                Yearly
              </button>
              <button
                className={view === "Quarterly" ? "active" : ""}
                onClick={() => setView("Quarterly")}
              >
                Quarterly
              </button>
            </div>

            {/* Dropdown */}
            <div className="debt__dropdown">
              <div className="custom-select">
                <select value={year} onChange={(e) => setYear(e.target.value)}>
                  {years.map((y, idx) => (
                    <option key={idx} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="debt__table">
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                {view === "Quarterly" ? (
                  <>
                    <th>Q1</th>
                    <th>Q2</th>
                    <th>Q3</th>
                    <th>Q4</th>
                  </>
                ) : (
                  <th>Value</th>
                )}
              </tr>
            </thead>
            <tbody>
              {debtData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.metric}</td>
                  {row.values.map((val, i) => (
                    <td key={i}>{val}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Debt;
